import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp,
  Download,
  Email,
  BarChart,
  PieChart,
  Timeline
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { salesAPI, purchaseAPI, orderAPI, reportsAPI } from '../services/api';

const Reports = () => {
  const [reportType, setReportType] = useState('sales');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [salesData, setSalesData] = useState([]);
  const [purchaseData, setPurchaseData] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ open: false, message: '', severity: 'success' }), 5000);
  };

  const reportTypes = [
    { value: 'sales', label: 'Sales Report', color: 'primary' },
    { value: 'purchase', label: 'Purchase Report', color: 'secondary' },
    { value: 'order', label: 'Order Report', color: 'info' },
    { value: 'financial', label: 'Financial Summary', color: 'success' },
  ];

  useEffect(() => {
    fetchReportData();
  }, [reportType, dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const [salesRes, purchaseRes, orderRes] = await Promise.all([
        salesAPI.getSales(),
        purchaseAPI.getPurchases(),
        orderAPI.getOrders()
      ]);

      setSalesData(salesRes.data);
      setPurchaseData(purchaseRes.data);
      setOrderData(orderRes.data);

      const salesStats = await salesAPI.getStats();
      const purchaseStats = await purchaseAPI.getStats();
      const orderStats = await orderAPI.getStats();

      setStats({
        sales: salesStats.data,
        purchase: purchaseStats.data,
        order: orderStats.data
      });

    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = () => {
    let filteredData = [];
    if (reportType === 'sales') filteredData = salesData;
    if (reportType === 'purchase') filteredData = purchaseData;
    if (reportType === 'order') filteredData = orderData;

    if (dateRange.startDate && dateRange.endDate) {
      filteredData = filteredData.filter(item => {
        const itemDate = new Date(item.created_at);
        return itemDate >= dateRange.startDate && itemDate <= dateRange.endDate;
      });
    }

    return filteredData;
  };

  const exportApiForType = {
    sales: salesAPI,
    purchase: purchaseAPI,
    order: orderAPI,
  };

  const fetchExportBlob = async () => {
    const exportApi = exportApiForType[reportType];
    if (!exportApi) return null;
    const res = await exportApi.exportExcel();
    return new Blob([res.data]);
  };

  const exportToExcel = async () => {
    if (!exportApiForType[reportType]) return;
    setExporting(true);
    try {
      const blob = await fetchExportBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `${reportType}_report.xlsx`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showAlert('Report exported');
    } catch (error) {
      showAlert('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const openEmailDialog = () => {
    if (!exportApiForType[reportType]) return;
    setEmailRecipients('');
    setEmailMessage('');
    setEmailDialogOpen(true);
  };

  const sendEmailReport = async () => {
    const recipients = emailRecipients
      .split(',')
      .map(r => r.trim())
      .filter(Boolean);

    if (recipients.length === 0) {
      showAlert('Enter at least one recipient email', 'error');
      return;
    }

    setEmailSending(true);
    try {
      const blob = await fetchExportBlob();
      const reportLabel = reportTypes.find(t => t.value === reportType)?.label || 'Report';

      const formData = new FormData();
      formData.append('file', blob, `${reportType}_report.xlsx`);
      formData.append('recipients', recipients.join(','));
      formData.append('subject', `${reportLabel} — ${new Date().toLocaleDateString()}`);
      formData.append('message', emailMessage);

      await reportsAPI.emailReport(formData);
      showAlert('Email sent successfully');
      setEmailDialogOpen(false);
    } catch (error) {
      const msg = error?.response?.data?.error || 'Failed to send email';
      showAlert(msg, 'error');
    } finally {
      setEmailSending(false);
    }
  };

  const calculateFinancialSummary = () => {
    const totalSales = salesData.reduce((sum, sale) => 
      sum + (parseFloat(sale.final_payment_received_from_client) || 0), 0
    );
    
    const totalPurchases = purchaseData.reduce((sum, purchase) => 
      sum + (parseFloat(purchase.final_payment_release_to_vendor) || 0), 0
    );

    const totalOrders = orderData.reduce((sum, order) => 
      sum + (parseFloat(order.total_amount) || 0), 0
    );

    return {
      totalSales,
      totalPurchases,
      totalOrders,
      profit: totalSales - totalPurchases,
      pendingPayments: purchaseData.reduce((sum, purchase) => 
        sum + (parseFloat(purchase.pending_payments_to_vendor) || 0), 0
      )
    };
  };

  const reportData = generateReport();
  const financialData = calculateFinancialSummary();

  const canExportOrEmail = Boolean(exportApiForType[reportType]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl">
        {alert.open && (
          <Alert severity={alert.severity} sx={{ mb: 2 }}>
            {alert.message}
          </Alert>
        )}

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Reports & Analytics
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Generate comprehensive reports and analyze business performance
          </Typography>
        </Box>

        {/* Report Controls */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    label="Report Type"
                  >
                    {reportTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="Start Date"
                  value={dateRange.startDate}
                  onChange={(newValue) => setDateRange(prev => ({...prev, startDate: newValue}))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="End Date"
                  value={dateRange.endDate}
                  onChange={(newValue) => setDateRange(prev => ({...prev, endDate: newValue}))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={canExportOrEmail ? '' : 'Not available for Financial Summary yet'}>
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={exporting ? <CircularProgress size={16} /> : <Download />}
                        onClick={exportToExcel}
                        disabled={!canExportOrEmail || exporting}
                      >
                        Export
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={canExportOrEmail ? '' : 'Not available for Financial Summary yet'}>
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<Email />}
                        onClick={openEmailDialog}
                        disabled={!canExportOrEmail}
                      >
                        Email
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Email Report Dialog */}
        <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Email Report</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Recipient email(s)"
                placeholder="jane@example.com, john@example.com"
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                fullWidth
                autoFocus
              />
              <TextField
                label="Message (optional)"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                fullWidth
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailDialogOpen(false)} disabled={emailSending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={sendEmailReport}
              disabled={emailSending}
              startIcon={emailSending ? <CircularProgress size={16} /> : <Email />}
            >
              Send
            </Button>
          </DialogActions>
        </Dialog>

        {/* Financial Summary Cards */}
        {reportType === 'financial' && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[
              { label: 'Total Sales', value: financialData.totalSales, icon: <TrendingUp />, color: 'success' },
              { label: 'Total Purchases', value: financialData.totalPurchases, icon: <TrendingUp />, color: 'warning' },
              { label: 'Total Orders', value: financialData.totalOrders, icon: <BarChart />, color: 'info' },
              { label: 'Net Profit', value: financialData.profit, icon: <PieChart />, color: 'success' },
              { label: 'Pending Payments', value: financialData.pendingPayments, icon: <Timeline />, color: 'error' },
            ].map((stat, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={index}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="textSecondary" gutterBottom variant="overline">
                          {stat.label}
                        </Typography>
                        <Typography variant="h4" component="div" color={`${stat.color}.main`}>
                          ₹{stat.value.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ color: `${stat.color}.main`, fontSize: 40 }}>
                        {stat.icon}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Report Data Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {reportTypes.find(t => t.value === reportType)?.label} - {reportData.length} records found
            </Typography>
            
            <TableContainer component={Paper} elevation={1}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    {reportType === 'sales' && (
                      <>
                        <TableCell><strong>SR No</strong></TableCell>
                        <TableCell><strong>Vessel Name</strong></TableCell>
                        <TableCell><strong>Final Amount</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Delivery Date</strong></TableCell>
                      </>
                    )}
                    {reportType === 'purchase' && (
                      <>
                        <TableCell><strong>PR No</strong></TableCell>
                        <TableCell><strong>Vessel Name</strong></TableCell>
                        <TableCell><strong>Vendor</strong></TableCell>
                        <TableCell><strong>Total Payment</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                      </>
                    )}
                    {reportType === 'order' && (
                      <>
                        <TableCell><strong>Order No</strong></TableCell>
                        <TableCell><strong>Vessel Name</strong></TableCell>
                        <TableCell><strong>Total Amount</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Delivery Date</strong></TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.slice(0, 10).map((item, index) => (
                    <TableRow key={index}>
                      {reportType === 'sales' && (
                        <>
                          <TableCell>{item.sr_no}</TableCell>
                          <TableCell>{item.vessel_name}</TableCell>
                          <TableCell>₹{(item.final_payment_received_from_client || 0).toLocaleString()}</TableCell>
                          <TableCell>
                            <Chip 
                              label={item.status} 
                              color={
                                item.status === 'delivered' ? 'success' :
                                item.status === 'pending' ? 'warning' : 'info'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {item.commited_delivery_date_to_client 
                              ? new Date(item.commited_delivery_date_to_client).toLocaleDateString()
                              : '-'
                            }
                          </TableCell>
                        </>
                      )}
                      {reportType === 'purchase' && (
                        <>
                          <TableCell>{item.pr_no}</TableCell>
                          <TableCell>{item.vessel_name}</TableCell>
                          <TableCell>{item.vendor_name}</TableCell>
                          <TableCell>₹{(item.final_payment_release_to_vendor || 0).toLocaleString()}</TableCell>
                          <TableCell>
                            <Chip 
                              label={item.status} 
                              color={
                                item.status === 'completed' ? 'success' :
                                item.status === 'pending' ? 'warning' : 'info'
                              }
                              size="small"
                            />
                          </TableCell>
                        </>
                      )}
                      {reportType === 'order' && (
                        <>
                          <TableCell>{item.order_no}</TableCell>
                          <TableCell>{item.vessel_name}</TableCell>
                          <TableCell>₹{(item.total_amount || 0).toLocaleString()}</TableCell>
                          <TableCell>
                            <Chip 
                              label={item.current_status} 
                              color={
                                item.current_status === 'delivered' ? 'success' :
                                item.current_status === 'pending' ? 'warning' : 'info'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {item.commited_delivery_date 
                              ? new Date(item.commited_delivery_date).toLocaleDateString()
                              : '-'
                            }
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {reportData.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="textSecondary">
                  No data found for the selected criteria
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </LocalizationProvider>
  );
};

export default Reports;